export interface BriefRecord {
  body: string
  updated_at: string
  updated_by_name: string | null
}

export interface BotBrief {
  bot_name: string
  bot_color: string
  company_id: string | null
  company_name: string | null
  company_initials: string | null
  role: BriefRecord | null
  company: BriefRecord | null
}

type Kind = "role" | "company"

const LIMIT = 1000
const STALE_DAYS = 45
const COLLAPSED_KEY = "callBriefCollapsed"
// the handoff's regex, plus (?!\/\/) so a line starting with a URL isn't split at "https:"
const KV = /^([^:/]{1,24}):(?!\/\/)\s*(.*)$/
const TEMPLATE: Record<Kind, string> = {
  role: "Salary: \nMust-haves: \nNice to have: \nDeal-breakers: \nSelling points: \nNext step: ",
  company: "About: \nOffice & remote: \nCulture: \nSelling points: "
}
const PLACEHOLDER: Record<Kind, string> = {
  role: "Salary: 55–65k SEK/month\nMust-haves: …\nNext step: …",
  company: "About: …\nOffice & remote: …\nSelling points: …"
}
const EMPTY: Record<Kind, string> = {
  role: "No role brief for this campaign yet. Add the salary range, must-haves and next step.",
  company: "No company brief yet. Add what the company does, office and selling points."
}

const ICON = {
  shield: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z"/><path d="M9.5 12l1.8 1.8L15 10"/></svg>',
  pen: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z"/></svg>',
  chev: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>',
  plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>'
}

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1"
  } catch (_e) {
    return false
  }
}

function saveCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0")
  } catch (_e) {
    // a blocked storage only loses the remembered state
  }
}

function esc(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

// Mirrors CallBriefs::Save's normalising, so "unchanged" means the same thing on both sides.
function normalise(body: string): string {
  return body.replace(/\r\n?/g, "\n").split("\n").map(line => line.replace(/\s+$/, "")).join("\n").trim()
}

function lines(body: string): string[] {
  return String(body || "").split("\n").map(line => line.trim()).filter(Boolean)
}

// A "Label:" line with nothing after it is a leftover template heading and is dropped.
function content(body: string | undefined): string[] {
  return lines(body || "").filter(line => {
    const match = line.match(KV)
    return !match || match[2] !== ""
  })
}

function ageDays(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
}

const RELATIVE_UNITS: [string, number][] = [["day", 86400], ["hour", 3600], ["minute", 60], ["second", 1]]

// "just now" under 10s, "N <unit>s ago" up to 7 days, the date and time after that.
function formatRelativeTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""

  // a future stamp is clock skew between server and browser
  const elapsed = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000))
  if (elapsed < 10) return "just now"

  if (elapsed <= 7 * 86400) {
    const [unit, seconds] = RELATIVE_UNITS.find(([, size]) => elapsed >= size)!
    const count = Math.floor(elapsed / seconds)
    return `${count} ${unit}${count === 1 ? "" : "s"} ago`
  }

  return date.toLocaleString(undefined, {year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"})
}

function kv(body: string): string {
  const rows = content(body).map(line => {
    const match = line.match(KV)
    if (!match) return `<div class="p">${esc(line)}</div>`
    const key = /^(salary|lön)/i.test(match[1]) ? " key" : ""
    return `<div class="k">${esc(match[1])}</div><div class="v${key}">${esc(match[2])}</div>`
  })
  return `<div class="brief-kv">${rows.join("")}</div>`
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
}

// One per call session: the GET runs once, and saves update the shared cache so the
// next candidates in the same campaign or company show the new text.
export class CallBriefSession {
  private briefs: Promise<Record<string, BotBrief>>
  private cache: Record<string, BotBrief> = {}

  constructor(private url: string, botIds: string[]) {
    const params = new URLSearchParams()
    Array.from(new Set(botIds.filter(Boolean))).forEach(id => params.append("bot_ids[]", id))
    this.briefs = fetch(`${url}?${params}`, { headers: { Accept: "application/json" } })
      .then(response => (response.ok ? response.json() : Promise.reject(new Error(String(response.status)))))
      .then((data: Record<string, BotBrief>) => (this.cache = data))
  }

  // Fills a card's slot; the previous candidate's bot drives the NEW CAMPAIGN / NEW CLIENT chips.
  mount(slot: HTMLElement, prevBotId: string | null): void {
    const botId = slot.dataset.botId || ""
    slot.innerHTML = '<div class="brief-note">Loading brief…</div>'
    this.briefs
      .then(() => {
        if (!slot.isConnected) return
        const brief = this.cache[botId]
        if (!brief) throw new Error("no brief entry")
        const prev = prevBotId ? this.cache[prevBotId] : undefined
        new CallBriefCard(slot, this, botId, {
          role: !!prevBotId && prevBotId !== botId,
          company: !!prev && prev.company_id !== brief.company_id
        })
      })
      .catch(() => {
        if (slot.isConnected) slot.innerHTML = '<div class="brief-note">Brief unavailable.</div>'
      })
  }

  get(botId: string): BotBrief | undefined {
    return this.cache[botId]
  }

  async save(botId: string, kind: Kind, body: string): Promise<void> {
    const brief = this.cache[botId]
    const id = kind === "role" ? botId : brief?.company_id
    if (!brief || !id) throw new Error("The brief could not be saved.")

    let response: Response
    try {
      response = await fetch(this.url, {
        method: "PATCH",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ briefable_type: kind === "role" ? "Bot" : "UserCompany", briefable_id: id, body })
      })
    } catch (_e) {
      throw new Error("The brief could not be saved. Check your connection and try again.")
    }

    const data: any = await response.json().catch(() => null)
    if (response.status === 403) throw new Error("Only intro.io admins can edit the brief. Reload the page.")
    if (!response.ok) throw new Error(data?.error || "The brief could not be saved.")

    const saved: BriefRecord | null = data?.brief || null
    if (kind === "role") {
      brief.role = saved
    } else {
      Object.keys(this.cache).forEach(key => {
        if (this.cache[key].company_id === brief.company_id) this.cache[key].company = saved
      })
    }
  }
}

// One card's slot. Its listeners live on the slot, so they go away with the card's HTML.
class CallBriefCard {
  private collapsed = readCollapsed()
  private editing = false
  private saving = false
  private flash = true

  constructor(
    private slot: HTMLElement,
    private session: CallBriefSession,
    private botId: string,
    private readonly changed: Record<Kind, boolean>
  ) {
    slot.addEventListener("click", event => this.click(event))
    slot.addEventListener("keydown", event => this.keydown(event))
    slot.addEventListener("input", event => this.input(event))
    this.render()
    this.flash = false
  }

  private brief(): BotBrief {
    return this.session.get(this.botId) as BotBrief
  }

  private record(kind: Kind): BriefRecord | null {
    return this.brief()[kind]
  }

  private hasCompany(): boolean {
    return !!this.brief().company_id
  }

  private kinds(): Kind[] {
    return this.hasCompany() ? ["role", "company"] : ["role"]
  }

  private render(focus?: Kind): void {
    const brief = this.brief()
    const chip = this.changed.company ? "New client" : this.changed.role ? "New campaign" : ""
    const cols = this.editing
      ? this.kinds().map(kind => this.editCol(kind)).join("")
      : (["role", "company"] as Kind[]).map(kind => this.viewCol(kind)).join("")

    this.slot.classList.toggle("collapsed", this.collapsed)
    this.slot.classList.toggle("editing", this.editing)
    this.slot.innerHTML =
      '<div class="brief-head"><h4>Client brief</h4>' +
      `<span class="admin-badge" title="Only intro.io admins see this. It is never sent to client users.">${ICON.shield}Admin only</span>` +
      (chip ? `<span class="brief-chg brief-chg-head">${chip}</span>` : "") +
      `<span class="spacer"></span><span class="brief-peek">${esc(this.peek())}</span>` +
      `<button type="button" class="brief-ibtn" data-brief-edit title="Edit brief" aria-label="Edit brief">${ICON.pen}</button>` +
      `<button type="button" class="brief-ibtn" data-brief-toggle title="${this.collapsed ? "Expand" : "Collapse"}" aria-label="Show or hide brief" aria-expanded="${!this.collapsed}">${ICON.chev}</button></div>` +
      `<div class="brief-main"><div class="brief-cols">${cols}</div>` +
      (this.editing
        ? `<div class="brief-foot"><span class="hint">Saved to the campaign and the company, so everyone calling for ${esc(brief.bot_name)} sees it. Write “Label: text” to get a row. ⌘↵ saves.</span>` +
          '<span class="brief-error" data-brief-error role="status" aria-live="polite"></span>' +
          '<button type="button" class="btn btn-ghost" data-brief-cancel>Cancel</button><button type="button" class="btn btn-green" data-brief-save>Save brief</button></div>'
        : "") +
      "</div>"

    if (!this.editing) return
    this.kinds().forEach(kind => {
      const textarea = this.textarea(kind)
      if (textarea) {
        this.count(textarea)
        this.grow(textarea)
      }
    })
    if (focus) {
      const textarea = this.textarea(focus)
      if (textarea) this.caret(textarea, textarea.value.length)
    }
  }

  private src(kind: Kind): string {
    const brief = this.brief()
    const head =
      kind === "role"
        ? `<span class="dot" style="background:${esc(brief.bot_color || "var(--brand)")}"></span><b>${esc(brief.bot_name || "Campaign")}</b><span class="lvl">Role</span>`
        : `<span class="co">${esc(brief.company_initials || "?")}</span><b>${esc(brief.company_name || "No company")}</b><span class="lvl">Company</span>`
    const chip = this.changed[kind] && !this.editing ? `<span class="brief-chg">${kind === "role" ? "New campaign" : "New client"}</span>` : ""
    return `<div class="brief-src">${head}${chip}</div>`
  }

  private viewCol(kind: Kind): string {
    const record = this.record(kind)
    const flash = this.flash && this.changed[kind] ? " flash" : ""
    let body: string

    if (record && content(record.body).length) {
      const days = ageDays(record.updated_at)
      const by = record.updated_by_name ? ` · ${esc(record.updated_by_name)}` : ""
      const stale = days > STALE_DAYS ? ' · <span class="brief-stale">May be outdated</span>' : ""
      body = `${kv(record.body)}<div class="brief-meta">Updated ${formatRelativeTime(record.updated_at)}${by}${stale}</div>`
    } else if (kind === "company" && !this.hasCompany()) {
      body = '<div class="brief-empty">This campaign has no client company.</div>'
    } else {
      body =
        `<div class="brief-empty">${EMPTY[kind]}` +
        `<div><button type="button" class="btn btn-sm" data-brief-add="${kind}">${ICON.plus}Add brief</button></div></div>`
    }
    return `<div class="brief-col${flash}" data-kind="${kind}">${this.src(kind)}${body}</div>`
  }

  private editCol(kind: Kind): string {
    const label = kind === "role" ? "Role brief" : "Company brief"
    return (
      `<div class="brief-col" data-kind="${kind}">${this.src(kind)}` +
      `<textarea class="textarea brief-ta" data-brief-ta="${kind}" maxlength="${LIMIT}" placeholder="${esc(PLACEHOLDER[kind])}" aria-label="${label}">${esc(this.record(kind)?.body || "")}</textarea>` +
      '<div class="brief-ta-foot"><span class="spacer"></span><span data-brief-count></span></div></div>'
    )
  }

  // The first content line of the role brief (in practice the salary), else the company brief's.
  private peek(): string {
    return content(this.record("role")?.body)[0] || content(this.record("company")?.body)[0] || "No brief yet"
  }

  private click(event: Event): void {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button")
    if (!button || !this.slot.contains(button)) return

    if (button.hasAttribute("data-brief-toggle")) {
      this.collapsed = !this.collapsed
      saveCollapsed(this.collapsed)
      this.slot.classList.toggle("collapsed", this.collapsed)
      button.title = this.collapsed ? "Expand" : "Collapse"
      button.setAttribute("aria-expanded", String(!this.collapsed))
    } else if (button.hasAttribute("data-brief-edit")) {
      this.edit("role")
    } else if (button.hasAttribute("data-brief-add")) {
      const kind = button.dataset.briefAdd as Kind
      this.edit()
      this.insertTemplate(kind)
    } else if (button.hasAttribute("data-brief-cancel")) {
      this.cancel()
    } else if (button.hasAttribute("data-brief-save")) {
      this.save()
    }
  }

  // Esc must not reach the call panel's window listener, which would close the whole session.
  private keydown(event: KeyboardEvent): void {
    if (!this.editing) return
    if (event.key === "Escape") {
      event.stopPropagation()
      this.cancel()
    } else if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      this.save()
    }
  }

  private input(event: Event): void {
    const textarea = (event.target as HTMLElement).closest<HTMLTextAreaElement>("[data-brief-ta]")
    if (!textarea) return
    this.count(textarea)
    this.grow(textarea)
  }

  private edit(focus?: Kind): void {
    this.editing = true
    if (this.collapsed) {
      this.collapsed = false
      saveCollapsed(false)
    }
    this.render(focus)
  }

  private cancel(): void {
    if (this.saving) return
    this.editing = false
    this.render()
  }

  // Adds the template headings the text doesn't have yet and puts the caret after the first new label.
  private insertTemplate(kind: Kind): void {
    const textarea = this.textarea(kind)
    if (!textarea) return

    const have = lines(textarea.value).map(line => (line.match(KV)?.[1] || "").toLowerCase())
    const add = TEMPLATE[kind].split("\n").filter(line => !have.includes(line.replace(/:\s*$/, "").toLowerCase()))
    if (!add.length) {
      this.caret(textarea, textarea.value.length)
      return
    }

    const start = textarea.value.replace(/\s+$/, "")
    textarea.value = (start ? `${start}\n` : "") + add.join("\n")
    this.count(textarea)
    this.grow(textarea)
    this.caret(textarea, (start ? start.length + 1 : 0) + add[0].length)
  }

  // Only the sides that changed are written, so an unchanged side keeps its "Updated" stamp.
  private async save(): Promise<void> {
    if (this.saving) return

    const changes = this.kinds()
      .map(kind => ({ kind, body: normalise(this.textarea(kind)?.value || "") }))
      .filter(({ kind, body }) => body !== normalise(this.record(kind)?.body || ""))
    if (!changes.length) {
      this.cancel()
      return
    }

    this.saving = true
    this.setBusy(true)
    // a side that saved stays saved in the cache even when the other side fails
    const errors = await Promise.all(
      changes.map(({ kind, body }) => this.session.save(this.botId, kind, body).then(() => "", (error: Error) => error.message))
    )
    this.saving = false

    const failed = errors.find(Boolean)
    if (!failed) {
      this.editing = false
      this.render()
      return
    }

    this.setBusy(false)
    const error = this.slot.querySelector<HTMLElement>("[data-brief-error]")
    if (error) error.textContent = failed
  }

  private setBusy(busy: boolean): void {
    this.slot.querySelectorAll<HTMLButtonElement>("[data-brief-save], [data-brief-cancel]").forEach(button => {
      button.disabled = busy
    })
    const save = this.slot.querySelector<HTMLButtonElement>("[data-brief-save]")
    if (save) save.textContent = busy ? "Saving…" : "Save brief"
  }

  private textarea(kind: Kind): HTMLTextAreaElement | null {
    return this.slot.querySelector<HTMLTextAreaElement>(`[data-brief-ta="${kind}"]`)
  }

  private count(textarea: HTMLTextAreaElement): void {
    const counter = textarea.parentElement?.querySelector<HTMLElement>("[data-brief-count]")
    if (!counter) return
    counter.textContent = `${textarea.value.length} / ${LIMIT}`
    counter.classList.toggle("over", textarea.value.length > LIMIT * 0.9)
  }

  private grow(textarea: HTMLTextAreaElement): void {
    textarea.style.height = "auto"
    textarea.style.height = `${Math.min(360, Math.max(156, textarea.scrollHeight + 2))}px`
  }

  private caret(textarea: HTMLTextAreaElement, pos: number): void {
    textarea.focus()
    textarea.setSelectionRange(pos, pos)
  }
}
